import {KAError} from "./ka-error.ts";
import {lazyLog as Log} from "./lazy-log.ts";
import {Errors, type ErrorKind} from "./types.ts";

// Use logUnreachableCode to make TypeScript prove that a branch of code is
// unreachable because all possible values of a type have been handled in
// other branches. See log-unreachable-code.typestest.ts for usage examples.
//
// The "empty" type is a special type that TypeScript uses when a union type has
// been refined exhaustively, meaning that it has already ruled out every
// type in the union. We use this type here to cause TypeScript to alert us if
// we expect to have handled every possible variant of a value, but we've
// forgotten something.
//
// It's possible for TypeScript's exhaustiveness proof to be wrong if the declared
// types don't match the actual types at runtime. This can happen if you use
// the `any` type. So we log an error here if the supposedly unreachable code
// is actually executed.
//
// Sometimes, you want to throw an error if the "unreachable" code is
// executed--e.g. because there's no sensible default value your function can
// return. For convenience, logUnreachableCode returns a KAError that you can
// throw if you choose.
export function logUnreachableCode(
    message: string,
    impossibleValue: never,
    kind: ErrorKind = Errors.Internal,
): KAError {
    Log.error(`${message}: ${impossibleValue}`, kind);
    return new KAError(`${message}: ${impossibleValue}`, kind);
}
